import { createContextApi, CACHE_POLICY, providesList, createCursorPagedEndpoint, providesOne, emitPlatformEvent, invalidatesOne, registerCrossContextInvalidation } from '@quickbee/store';
import { getContextClient } from '@quickbee/api-client';

// src/index.ts
var REDEMPTION_TAGS = ["Redemption", "RedeemableBalance", "Coupon"];
var certificateDownloadRequest = (redemptionId) => ({
  url: `/redemptions/${redemptionId}/certificate`,
  method: "GET",
  responseType: "binary"
});
var redemptionEndpoints = (build) => ({
  listRedemptions: build.query({
    query: (params) => ({ url: "/redemptions", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Redemption", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The same register, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listRedemptions} drops `cursor` from its cache key and appends every response into one
   * entry, which is right behind a "load more" and wrong behind a numbered pager: forward would
   * grow the rendered set rather than replace it, and backward would re-append a walked cursor's
   * rows. Keeping the cursor in the key makes each page its own entry and *Previous* free.
   *
   * Read by `useCursorPager` on the Portfolio PWA's Redemptions register. Note that this endpoint
   * could not serve a second page at all until `RedemptionRequestRepositoryAdapter` was moved onto
   * the offset-cursor scheme — it built every page at offset zero with a `null` cursor, so a saver
   * past their newest `size` requests could not reach the rest through any client.
   */
  listRedemptionsPage: build.query({
    query: (params) => ({ url: "/redemptions", params }),
    providesTags: (result) => providesList("Redemption", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  getRedemption: build.query({
    query: (id) => ({ url: `/redemptions/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Redemption", id)
  }),
  getRedeemableBalances: build.query({
    query: () => ({ url: "/redemptions/redeemable-balances" }),
    providesTags: [{ type: "RedeemableBalance", id: "LIST" }],
    // A balance the customer is about to redeem against, priced at the live rate.
    keepUnusedDataFor: CACHE_POLICY.financialBalance.keepUnusedDataFor
  }),
  /**
   * ADR-008 step 1 — **reserves only**. For `MERCHANT_COUPON` no gold moves and
   * nothing is priced: the response comes back `RESERVED` with a null
   * `redemptionValue`. For `BANK_TRANSFER` the escrow debit does happen here
   * (ADR-005), which is why the redeemable balance is invalidated either way.
   */
  createRedemption: build.mutation({
    query: ({ draftId, ...body }) => ({
      url: "/redemptions",
      method: "POST",
      body,
      // Keyed on the intent, not the attempt: a customer who taps "Redeem"
      // twice reserves once.
      idempotencyKey: `redeem:${body.schemeEnrollmentId}:${draftId}`
    }),
    invalidatesTags: [
      { type: "Redemption", id: "LIST" },
      { type: "RedeemableBalance", id: "LIST" }
    ],
    async onQueryStarted(_args, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("redemption.requested", { redemptionId: data.id });
      } catch {
      }
    }
  }),
  /**
   * ADR-008 step 2 — **the buy-back**. Prices the gold at the live rate, debits
   * it, and returns the issued coupon carrying the now-frozen rupee value.
   *
   * Returns a `CouponResponse`, not the parent request: the request must be
   * re-read (or its tag invalidated, as here) to see the new
   * `AWAITING_REDEMPTION` status. Repeat calls are safe — the handler
   * short-circuits and re-renders the existing coupon rather than debiting
   * again — but the redeemable balance genuinely changed, so it is invalidated.
   */
  generateCertificate: build.mutation({
    query: ({ id, ...body }) => ({
      url: `/redemptions/${id}/certificate`,
      method: "POST",
      body,
      idempotencyKey: `redemption-certificate:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "Coupon", id: "LIST" },
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  cancelRedemption: build.mutation({
    query: ({ id, reason }) => ({
      url: `/redemptions/${id}/cancel`,
      method: "POST",
      // The body is optional server-side, but `reason` is the only thing in it —
      // sending `{}` keeps one request shape instead of two.
      body: reason === void 0 ? {} : { reason },
      idempotencyKey: `redemption-cancel:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  /** Ops action. `:tenant`-scoped — takes no body at all. */
  approveRedemption: build.mutation({
    query: ({ id }) => ({
      url: `/redemptions/${id}/approve`,
      method: "POST",
      idempotencyKey: `redemption-approve:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => invalidatesOne("Redemption", id)
  }),
  /** Ops action. `reason` is required — the server rejects a blank one. */
  rejectRedemption: build.mutation({
    query: ({ id, reason, reasonCode }) => ({
      url: `/redemptions/${id}/reject`,
      method: "POST",
      // `reasonCode` is optional (B10) and omitted rather than sent as `undefined`, so
      // nothing depends on how the transport happens to serialise an absent field.
      body: reasonCode === void 0 ? { reason } : { reason, reasonCode },
      idempotencyKey: `redemption-reject:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      // A reject reverses the ledger debit wherever one exists (INV-11).
      { type: "RedeemableBalance", id: "LIST" }
    ]
  }),
  completeRedemption: build.mutation({
    query: ({ id }) => ({
      url: `/redemptions/${id}/complete`,
      method: "POST",
      idempotencyKey: `redemption-complete:${id}`
    }),
    invalidatesTags: (_r, _e, { id }) => [
      ...invalidatesOne("Redemption", id),
      { type: "RedeemableBalance", id: "LIST" }
    ],
    async onQueryStarted({ id }, { queryFulfilled }) {
      try {
        await queryFulfilled;
        emitPlatformEvent("redemption.settled", { redemptionId: id });
      } catch {
      }
    }
  }),
  // ─── Coupons (POS / merchant counter) ───────────────────────────────────────
  /**
   * A read dressed as a `POST` so the code never lands in a URL or an access
   * log. Not `@IdempotentRequest` server-side, so it must opt out — and a
   * not-currently-redeemable coupon comes back `200 OK` with `valid: false`,
   * never as a thrown error.
   */
  verifyCoupon: build.mutation({
    query: ({ code }) => ({
      url: "/redemptions/coupons/verify",
      method: "POST",
      body: { code },
      skipIdempotencyKey: true
    })
  }),
  /**
   * `202 Accepted` with an empty envelope — this guarantees the send was
   * *initiated*, not delivered. Deliberately **not** idempotency-keyed: a resend
   * is a legitimate repeat action, and the abuse control is the server's own
   * resend cooldown (a `429`), not a swallowed duplicate.
   */
  sendCouponOtp: build.mutation({
    query: ({ code }) => ({
      url: `/redemptions/coupons/${code}/otp`,
      method: "POST",
      skipIdempotencyKey: true
    })
  }),
  /**
   * The money-moving action at the counter, and the most realistic double-tap
   * hazard in the platform. Invalidates by the coupon's **id** taken off the
   * response — the caller only holds a code, and the cache is keyed by id.
   */
  /**
   * Burns a coupon at the counter.
   *
   * `consumedValue` is the bill the coupon was presented against. **Omitting it
   * burns the coupon whole** — the correct answer for a till with no bill
   * lookup, and the wrong one at a counter that knows the bill was smaller than
   * the coupon: anything unspent is credited straight back to the saver's
   * account as residual, and a coupon burned whole against a smaller bill loses
   * the customer the difference.
   */
  redeemCoupon: build.mutation({
    query: ({ code, otpCode, pin, consumedValue, storeId }) => ({
      url: `/redemptions/coupons/${code}/redeem`,
      method: "POST",
      // Both authorisation factors travel together (§2.1): the OTP proves the
      // customer consented, the PIN proves the counter holds the document.
      body: {
        otpCode,
        pin,
        ...consumedValue === void 0 ? {} : { consumedValue },
        ...storeId === void 0 ? {} : { storeId }
      },
      idempotencyKey: `coupon-redeem:${code}`
    }),
    invalidatesTags: (result) => [
      { type: "Coupon", id: "LIST" },
      { type: "Redemption", id: "LIST" },
      ...result ? [{ type: "Coupon", id: result.id }] : [],
      ...result ? invalidatesOne("Redemption", result.redemptionRequestId) : []
    ],
    async onQueryStarted(_args, { queryFulfilled }) {
      try {
        const { data } = await queryFulfilled;
        emitPlatformEvent("redemption.settled", { redemptionId: data.redemptionRequestId });
      } catch {
      }
    }
  }),
  getCoupon: build.query({
    query: (id) => ({ url: `/redemptions/coupons/${id}` }),
    providesTags: (_r, _e, id) => providesOne("Coupon", id)
  }),
  listCoupons: build.query({
    query: (params) => ({ url: "/redemptions/coupons", params }),
    ...createCursorPagedEndpoint(),
    providesTags: (result) => providesList("Coupon", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  }),
  /**
   * The same coupon register, paged **discretely** — one request, one page, no accumulation.
   *
   * {@link listCoupons} drops `cursor` from its cache key and appends every response into a single
   * entry, which is exactly right for a "load more" list and exactly wrong for a numbered pager:
   * paging forward would grow the rendered set instead of replacing it, and paging *back* would
   * re-fetch a cursor already walked and append its rows a second time.
   *
   * So this one keeps the cursor in the cache key. Each page is its own entry, which also makes
   * Previous free — a page already walked is answered from cache rather than re-fetched. It is what
   * `useCursorPager` reads; see `api-lender`'s `listLendersPage` and `api-audit`'s
   * `listEntriesPage`, where the same pair exists for the same reason.
   *
   * POS's register is the caller. It used to read `listCoupons` with `size: 100` and page inside the
   * window under a footer whose `1–10 of 100` was the count of rows fetched rather than the
   * tenant's — and `redemption-service` could not have served a second page even if it had asked
   * (`RedemptionCouponRepositoryAdapter` built every page at offset zero with a null cursor).
   *
   * A `size: 1` read of this endpoint is also how a tile counts without listing: the response
   * carries the tenant's own `pagination.total` for the filters in force, and one row crosses the
   * wire. It shares the `Coupon` tag with the register, so a burn at the counter moves the tiles and
   * the grid together.
   */
  listCouponsPage: build.query({
    query: (params) => ({ url: "/redemptions/coupons", params }),
    providesTags: (result) => providesList("Coupon", result),
    keepUnusedDataFor: CACHE_POLICY.transactionalList.keepUnusedDataFor
  })
});
function redemptionClient() {
  return getContextClient({ context: "redemption" });
}

// src/certificate.ts
function downloadRedemptionCertificate(redemptionId) {
  return redemptionClient().request(certificateDownloadRequest(redemptionId));
}
async function lookupPublicCoupon(code) {
  const client = getContextClient({ context: "redemption", publicClient: true });
  try {
    return await client.get(
      `/redemptions/public/coupons/${encodeURIComponent(code)}`,
      { publicRoute: true }
    );
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
}
function isNotFound(error) {
  return typeof error === "object" && error !== null && "status" in error && error.status === 404;
}

// src/guards.ts
var TERMINAL_REDEMPTION_STATUSES = [
  "COMPLETED",
  "REJECTED",
  "CANCELLED"
];
function isTerminalRedemptionStatus(status) {
  return TERMINAL_REDEMPTION_STATUSES.includes(status);
}
function isCancellableRedemptionStatus(status) {
  return status === "RESERVED" || status === "PENDING";
}
function canGenerateCertificate(status) {
  return status === "RESERVED";
}
var COUPON_CODE_PATTERN = /^[A-Z0-9]{16}$/;
function isCouponCode(value) {
  return COUPON_CODE_PATTERN.test(value);
}
function normalizeCouponCode(input) {
  return input.toUpperCase().replace(/[\s-]/g, "");
}
var COUPON_OTP_PATTERN = /^[0-9]{6}$/;
function isCouponOtp(value) {
  return COUPON_OTP_PATTERN.test(value);
}
var COUPON_PIN_PATTERN = /^[0-9]{4}$/;
function isCouponPin(value) {
  return COUPON_PIN_PATTERN.test(value);
}

// src/types.ts
var REDEMPTION_MAX_PAGE_SIZE = 100;

// src/index.ts
var redemptionApi = createContextApi({
  context: "redemption",
  reducerPath: "redemptionApi",
  tagTypes: REDEMPTION_TAGS,
  endpoints: redemptionEndpoints
});
var REDEMPTION_INVALIDATION_RULES = [
  { event: "ledger.posted", invalidate: () => [{ type: "RedeemableBalance", id: "LIST" }] },
  { event: "payment.captured", invalidate: () => [{ type: "RedeemableBalance", id: "LIST" }] }
];
function registerRedemptionInvalidation(store) {
  return registerCrossContextInvalidation({
    store,
    api: redemptionApi,
    rules: REDEMPTION_INVALIDATION_RULES
  });
}
var {
  useListRedemptionsQuery,
  useLazyListRedemptionsQuery,
  // The discrete twin: one page per cache entry, for a numbered pager (`useCursorPager`).
  useListRedemptionsPageQuery,
  useGetRedemptionQuery,
  useLazyGetRedemptionQuery,
  useGetRedeemableBalancesQuery,
  useCreateRedemptionMutation,
  useGenerateCertificateMutation,
  useCancelRedemptionMutation,
  useApproveRedemptionMutation,
  useRejectRedemptionMutation,
  useCompleteRedemptionMutation,
  useVerifyCouponMutation,
  useSendCouponOtpMutation,
  useRedeemCouponMutation,
  useGetCouponQuery,
  useListCouponsQuery,
  useLazyListCouponsQuery,
  useListCouponsPageQuery
} = redemptionApi;

export { COUPON_CODE_PATTERN, COUPON_OTP_PATTERN, COUPON_PIN_PATTERN, REDEMPTION_INVALIDATION_RULES, REDEMPTION_MAX_PAGE_SIZE, REDEMPTION_TAGS, TERMINAL_REDEMPTION_STATUSES, canGenerateCertificate, downloadRedemptionCertificate, isCancellableRedemptionStatus, isCouponCode, isCouponOtp, isCouponPin, isTerminalRedemptionStatus, lookupPublicCoupon, normalizeCouponCode, redemptionApi, registerRedemptionInvalidation, useApproveRedemptionMutation, useCancelRedemptionMutation, useCompleteRedemptionMutation, useCreateRedemptionMutation, useGenerateCertificateMutation, useGetCouponQuery, useGetRedeemableBalancesQuery, useGetRedemptionQuery, useLazyGetRedemptionQuery, useLazyListCouponsQuery, useLazyListRedemptionsQuery, useListCouponsPageQuery, useListCouponsQuery, useListRedemptionsPageQuery, useListRedemptionsQuery, useRedeemCouponMutation, useRejectRedemptionMutation, useSendCouponOtpMutation, useVerifyCouponMutation };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map